import { ATTRIBUTES, ATTRIBUTE_KEYS } from "@/lib/playerConstants";

// All valid criteria for Mode B & C
export const CRITERIA = [
  { key: "overall", label: "Gesamtbewertung" },
  ...ATTRIBUTES,
  { key: "alter", label: "Alter" },
];

export function ageFromGeburtsdatum(iso) {
  if (!iso) return 0;
  const [y, m, d] = iso.split("-").map(Number);
  const today = new Date();
  let age = today.getFullYear() - y;
  const beforeBirthday =
    today.getMonth() + 1 < m ||
    (today.getMonth() + 1 === m && today.getDate() < d);
  if (beforeBirthday) age -= 1;
  return age;
}

export function getCriterionValue(player, key) {
  if (key === "overall") return player.overall;
  if (key === "alter") return ageFromGeburtsdatum(player.geburtsdatum);
  if (ATTRIBUTE_KEYS.includes(key)) return player.attribute[key];
  return 0;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Genetic algorithm: produces highly balanced team assignments where each team
 * has near-equal average for the chosen criterion AND near-equal size (max
 * difference 1). Replaces previous greedy approach for Modus A & B.
 *
 * Population: 300 · Generations: 300 · Elitism: 10% · Mutation: ~8%
 * Early stop after 30 stagnant generations or near-zero fitness.
 */
export function balanceTeams(players, teamCount, criterionKey, randomize = false) {
  if (players.length === 0 || teamCount < 2) {
    return Array.from({ length: teamCount }, () => []);
  }
  // Touch randomize parameter (kept for API compatibility); GA itself is stochastic.
  void randomize;

  const N = players.length;
  const baseSize = Math.floor(N / teamCount);
  const rem = N % teamCount;
  // Target team sizes (sum to N, differ at most by 1)
  const targetSizes = Array.from({ length: teamCount }, (_, i) => baseSize + (i < rem ? 1 : 0));

  const values = players.map((p) => getCriterionValue(p, criterionKey));

  // Build a random chromosome that respects target team sizes
  const makeRandom = () => {
    const arr = [];
    for (let t = 0; t < teamCount; t++) {
      for (let i = 0; i < targetSizes[t]; i++) arr.push(t);
    }
    // Fisher-Yates shuffle
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // Standard deviation of team averages = fitness (lower is better)
  const fitness = (chromo) => {
    const sums = new Array(teamCount).fill(0);
    const counts = new Array(teamCount).fill(0);
    for (let i = 0; i < N; i++) {
      sums[chromo[i]] += values[i];
      counts[chromo[i]] += 1;
    }
    const avgs = sums.map((s, i) => (counts[i] > 0 ? s / counts[i] : 0));
    const mean = avgs.reduce((a, b) => a + b, 0) / teamCount;
    let variance = 0;
    for (const a of avgs) variance += (a - mean) * (a - mean);
    variance /= teamCount;
    let penalty = 0;
    for (let t = 0; t < teamCount; t++) {
      penalty += Math.abs(counts[t] - targetSizes[t]);
    }
    return Math.sqrt(variance) + penalty * 100; // large penalty for size violations
  };

  // Repair to satisfy team-size constraint
  const repair = (chromo) => {
    const counts = new Array(teamCount).fill(0);
    for (const t of chromo) counts[t]++;
    for (let over = 0; over < teamCount; over++) {
      while (counts[over] > targetSizes[over]) {
        const idx = chromo.indexOf(over);
        let target = -1;
        for (let t = 0; t < teamCount; t++) {
          if (counts[t] < targetSizes[t]) { target = t; break; }
        }
        if (target < 0) break;
        chromo[idx] = target;
        counts[over]--;
        counts[target]++;
      }
    }
    return chromo;
  };

  // Uniform-style crossover with repair
  const crossover = (a, b) => {
    const child = new Array(N);
    for (let i = 0; i < N; i++) child[i] = Math.random() < 0.5 ? a[i] : b[i];
    return repair(child);
  };

  // Mutation: swap two random positions (preserves sizes)
  const mutate = (chromo, rate) => {
    if (Math.random() >= rate) return chromo;
    const swaps = 1 + Math.floor(Math.random() * 2); // 1-2 swaps
    for (let s = 0; s < swaps; s++) {
      const i = Math.floor(Math.random() * N);
      let j = Math.floor(Math.random() * N);
      let guard = 0;
      while (chromo[i] === chromo[j] && guard++ < 10) j = Math.floor(Math.random() * N);
      [chromo[i], chromo[j]] = [chromo[j], chromo[i]];
    }
    return chromo;
  };

  // Initial population
  const POP_SIZE = 300;
  const GENERATIONS = 300;
  const ELITE = Math.max(1, Math.floor(POP_SIZE * 0.10));
  const MUTATION_RATE = 0.08;
  const STAGNATION_LIMIT = 30;

  let population = Array.from({ length: POP_SIZE }, () => makeRandom());
  let bestFit = Infinity;
  let bestChromo = population[0];
  let stagnant = 0;

  for (let gen = 0; gen < GENERATIONS; gen++) {
    const scored = population.map((c) => ({ c, f: fitness(c) })).sort((x, y) => x.f - y.f);
    if (scored[0].f < bestFit - 1e-9) {
      bestFit = scored[0].f;
      bestChromo = scored[0].c.slice();
      stagnant = 0;
    } else {
      stagnant++;
    }
    if (bestFit < 1e-9) break;
    if (stagnant >= STAGNATION_LIMIT) break;

    const newPop = [];
    // Elitism: keep best ELITE
    for (let i = 0; i < ELITE; i++) newPop.push(scored[i].c.slice());
    // Tournament-ish: pick from top 50%
    const poolSize = Math.floor(POP_SIZE / 2);
    while (newPop.length < POP_SIZE) {
      const a = scored[Math.floor(Math.random() * poolSize)].c;
      const b = scored[Math.floor(Math.random() * poolSize)].c;
      const child = crossover(a, b);
      newPop.push(mutate(child, MUTATION_RATE));
    }
    population = newPop;
  }

  // Convert best chromosome to teams array
  const teams = Array.from({ length: teamCount }, () => []);
  for (let i = 0; i < N; i++) teams[bestChromo[i]].push(players[i]);
  return teams;
}

/**
 * Training groups: sort players by criterion desc, then split into N
 * roughly-equal sequential chunks. Top players in group 1, etc.
 */
export function trainingGroups(players, groupCount, criterionKey) {
  const sorted = [...players].sort(
    (a, b) => getCriterionValue(b, criterionKey) - getCriterionValue(a, criterionKey)
  );
  const groups = Array.from({ length: groupCount }, () => []);
  const base = Math.floor(sorted.length / groupCount);
  const rem = sorted.length % groupCount;
  let idx = 0;
  for (let g = 0; g < groupCount; g++) {
    const size = base + (g < rem ? 1 : 0);
    for (let i = 0; i < size && idx < sorted.length; i++) {
      groups[g].push(sorted[idx++]);
    }
  }
  return groups;
}

export function teamStats(team) {
  if (team.length === 0) return { count: 0, avg: 0, sum: 0, avgAge: 0 };
  const sum = team.reduce((s, p) => s + p.overall, 0);
  const ages = team.map((p) => ageFromGeburtsdatum(p.geburtsdatum));
  const avgAge = ages.reduce((s, a) => s + a, 0) / ages.length;
  return {
    count: team.length,
    avg: Math.round((sum / team.length) * 10) / 10,
    sum: Math.round(sum * 10) / 10,
    avgAge: Math.round(avgAge * 10) / 10,
  };
}

export function criterionAvg(team, key) {
  if (team.length === 0) return 0;
  const s = team.reduce((acc, p) => acc + getCriterionValue(p, key), 0);
  return Math.round((s / team.length) * 10) / 10;
}

export function groupStats(group, key) {
  if (group.length === 0) return { count: 0, avg: 0, min: 0, max: 0 };
  const values = group.map((p) => getCriterionValue(p, key));
  const sum = values.reduce((s, v) => s + v, 0);
  return {
    count: group.length,
    avg: Math.round((sum / values.length) * 10) / 10,
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

export { shuffle };
